import AddIcon from '@mui/icons-material/Add'
import CancelIcon from '@mui/icons-material/Cancel'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import EditIcon from '@mui/icons-material/Edit'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import HistoryIcon from '@mui/icons-material/History'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import PublishIcon from '@mui/icons-material/Publish'
import RemoveIcon from '@mui/icons-material/Remove'
import SecurityIcon from '@mui/icons-material/Security'
import {
	Box,
	Breadcrumbs,
	Button,
	Chip,
	Divider,
	IconButton,
	Link,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Select,
	SelectChangeEvent,
	Tooltip,
	Typography,
} from '@mui/material'
import { MouseEvent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import { DialogWrapper } from '@/components'
import { StructureObjectDto } from '@/endpoints/models'
import {
	useAppContext,
	useAppDispatch,
	useAppStore,
	useAuthContext,
} from '@/hooks'
import { useRunSaveBeforeLeave } from '@/pages/User/pages/Home/context/SaveBeforeLeaveContext'
import { useGetAllNodeHistoryUsingGetQuery } from '@/rtkApi'
import { StructureDto } from '@/rtkApi/mddApiSliceGenerated'
import { lockNode } from '@/store/modules/node/actions'
import { loadNode } from '@/store/modules/node/general-actions'
import {
	cancelEditingTab,
	editTab,
	setTabPermanent,
} from '@/store/modules/tab/actions'
import { openTab } from '@/store/modules/tab/actions'
import {
	canUnlockStructure,
	getBreadcrumbs,
	hasNodePermissions,
	hasStructureEditPermission,
	hasStructureManageAccessPermission,
	hasStructureViewPermission,
	isEditable,
	isImportableNodeType,
	isLocked,
	isLockedByMe,
	shouldShowUnlockButton,
} from '@/utils'
import { toLocaleString } from '@/utils/numbers'

import { CancelEditModal } from '../CancelEditModal'
import { DeleteModal } from '../DeleteModal'
import { RestoreVersionDialog } from '../DetailTab/components/RestoreVersionDialog'
import { SelectEnv } from '../DetailTab/components/SelectEnv'
import { useReplaceVersionData } from '../DetailTab/hooks/useReplaceVersionData'
import { useSyncHistoryToDetailTab } from '../DetailTab/hooks/useSyncHistoryToDetailTab'
import { EntityTypeIcon } from '../EntityTypeIcon'
import { PushModal } from '../PushModal/PushModal'
import { ExportStructure } from '../TreePanel/components/ExportStructure'
import { ImportStructureModal } from '../TreePanel/components/ImportStructureModal'
import { StructureAclModal } from '../TreePanel/components/StructureAclModal'
import { UnlockModal } from '../UnlockModal'

// Collapsible Breadcrumbs with dropdown for hidden items
const MAX_VISIBLE_ITEMS = 3 // first + last 2 (including current)

type CollapsibleBreadcrumbsProps = {
	breadcrumbs: StructureDto[]
	currentNode: StructureDto | StructureObjectDto
	onBreadcrumbClick: (node: StructureDto) => void
}

const CollapsibleBreadcrumbs = ({ breadcrumbs, currentNode, onBreadcrumbClick }: CollapsibleBreadcrumbsProps) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
	const open = Boolean(anchorEl)

	const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget)
	}

	const handleCloseMenu = () => {
		setAnchorEl(null)
	}

	const handleMenuItemClick = (crumb: StructureDto) => {
		handleCloseMenu()
		onBreadcrumbClick(crumb)
	}

	// If breadcrumbs are short enough, show all
	if (breadcrumbs.length <= MAX_VISIBLE_ITEMS) {
		return (
			<Breadcrumbs
				separator={<NavigateNextIcon fontSize="small" sx={{ color: 'grey.400' }} />}
				sx={{
					'& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
					'& .MuiBreadcrumbs-li': { minWidth: 0 },
				}}
			>
				{breadcrumbs.map((crumb) => (
					<Link
						key={crumb.id}
						component="button"
						variant="body2"
						onClick={() => onBreadcrumbClick(crumb)}
						sx={{
							color: 'text.secondary',
							textDecoration: 'none',
							maxWidth: 100,
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
							display: 'block',
							'&:hover': { color: 'primary.main', textDecoration: 'underline' },
						}}
						title={crumb.name}
					>
						{crumb.name}
					</Link>
				))}
				<Box className="flex items-center gap-1.5">
					<EntityTypeIcon type={currentNode.type} />
					<Typography
						variant="subtitle1"
						fontWeight={600}
						sx={{
							maxWidth: 180,
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
						}}
						title={currentNode.name}
					>
						{currentNode.name}
					</Typography>
				</Box>
			</Breadcrumbs>
		)
	}

	// Split breadcrumbs: first item, hidden items (dropdown), last item
	const firstItem = breadcrumbs[0]
	const hiddenItems = breadcrumbs.slice(1, -1)
	const lastItem = breadcrumbs[breadcrumbs.length - 1]

	return (
		<>
			<Breadcrumbs
				separator={<NavigateNextIcon fontSize="small" sx={{ color: 'grey.400' }} />}
				sx={{
					'& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
					'& .MuiBreadcrumbs-li': { minWidth: 0 },
				}}
			>
				{/* First item */}
				<Link
					component="button"
					variant="body2"
					onClick={() => onBreadcrumbClick(firstItem)}
					sx={{
						color: 'text.secondary',
						textDecoration: 'none',
						maxWidth: 100,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
						display: 'block',
						'&:hover': { color: 'primary.main', textDecoration: 'underline' },
					}}
					title={firstItem.name}
				>
					{firstItem.name}
				</Link>

				{/* Dropdown trigger for hidden items */}
				<Tooltip title={hiddenItems.map(h => h.name).join(' > ')}>
					<Link
						component="button"
						variant="body2"
						onClick={handleOpenMenu}
						sx={{
							color: 'text.secondary',
							textDecoration: 'none',
							'&:hover': { color: 'primary.main' },
							cursor: 'pointer',
						}}
					>
						...
					</Link>
				</Tooltip>

				{/* Last breadcrumb item */}
				<Link
					component="button"
					variant="body2"
					onClick={() => onBreadcrumbClick(lastItem)}
					sx={{
						color: 'text.secondary',
						textDecoration: 'none',
						maxWidth: 100,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
						display: 'block',
						'&:hover': { color: 'primary.main', textDecoration: 'underline' },
					}}
					title={lastItem.name}
				>
					{lastItem.name}
				</Link>

				{/* Current node */}
				<Box className="flex items-center gap-1.5">
					<EntityTypeIcon type={currentNode.type} />
					<Typography
						variant="subtitle1"
						fontWeight={600}
						sx={{
							maxWidth: 180,
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
						}}
						title={currentNode.name}
					>
						{currentNode.name}
					</Typography>
				</Box>
			</Breadcrumbs>

			{/* Dropdown menu for hidden items */}
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleCloseMenu}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			>
				{hiddenItems.map((crumb) => (
					<MenuItem key={crumb.id} onClick={() => handleMenuItemClick(crumb)}>
						{crumb.name}
					</MenuItem>
				))}
			</Menu>
		</>
	)
}

type DetailHeaderWithHistoryProps = {
	additionalButtons?: ReactNode,
	additionalMenuItems?: ReactNode,
	afterPush?: () => void,
	/** When provided (e.g. from ElementDetailV2), a single edit status field is shown instead of the chip. */
	autosaveCountdown?: number | null
	beforePush?: () => Promise<void>,
	customDiagramWarning?: boolean,
	dirty?: boolean,
	editMode: boolean,
	node: StructureDto | StructureObjectDto,
	onCancel: () => void,
	onEdit: (node: StructureDto | StructureObjectDto) => Promise<void> | void,
	/** When false, hides unified edit status and the breadcrumb dirty/saved chip (autosave still runs in ElementDetailV2). */
	showEditSaveStatus?: boolean
}

export const DetailHeaderWithHistory = ({
	node,
	editMode,
	dirty,
	autosaveCountdown,
	showEditSaveStatus = true,
	onEdit,
	onCancel,
	beforePush,
	afterPush,
	customDiagramWarning,
	additionalMenuItems,
	additionalButtons,
}: DetailHeaderWithHistoryProps) => {
	const { t } = useAppContext()
	const auth = useAuthContext()
	const dispatch = useAppDispatch()
	const nodes = useAppStore((state) => state.node.nodes)
	const user = useAppStore((state) => state.auth.user)

	// History state - managed locally
	const [historyActive, setHistoryActive] = useState(false)
	const [selectedVersion, setSelectedVersion] = useState(1)

	const historyList = useGetAllNodeHistoryUsingGetQuery({ structureId: node.id })

	const { replaceVersionData, replacing } = useReplaceVersionData((v) => setSelectedVersion(v))

	const versions = useMemo(
		() => historyList.data?.map((history) => history.version) ?? [1],
		[historyList.data],
	)

	const currentVersionInfo = useMemo(() => {
		return historyList.data?.find((h) => h.version === selectedVersion)
	}, [historyList.data, selectedVersion])

	const currentVersionDeployments = useMemo(() => {
		return currentVersionInfo?.deployments
	}, [currentVersionInfo])

	const { versionMin, versionMax } = useMemo(() => {
		const versionMin = Math.min(...versions)
		const versionMax = Math.max(...versions)
		return { versionMin, versionMax }
	}, [versions])

	useEffect(() => {
		setSelectedVersion(versionMax)
	}, [versionMax])

	useSyncHistoryToDetailTab({
		historyActive,
		selectedVersion,
		versionMax,
	})

	const handleRestore = useCallback(async () => {
		await replaceVersionData()
		await historyList.refetch()
	}, [replaceVersionData, historyList])

	// Menu state
	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
	const menuOpen = Boolean(menuAnchor)
	const [download, setDownload] = useState(false)
	const [importModalOpen, setImportModalOpen] = useState(false)

	const [aclModalNode, setAclModalNode] = useState<StructureDto | null>(null)
	const [aclModalTargetId, setAclModalTargetId] = useState<number | null>(null)

	const handleMenuOpen = useCallback((event: MouseEvent<HTMLElement>) => {
		setMenuAnchor(event.currentTarget)
	}, [])

	const handleMenuClose = useCallback(() => {
		setMenuAnchor(null)
	}, [])

	const handleCloseAclModal = useCallback(() => {
		setAclModalNode(null)
		setAclModalTargetId(null)
	}, [])

	const handleAclMutated = useCallback(
		(structureId: number) => {
			void dispatch(loadNode(structureId))
		},
		[dispatch],
	)

	const runSaveBeforeLeave = useRunSaveBeforeLeave()
	const handleBreadcrumbClick = useCallback(
		async (breadcrumbNode: StructureDto) => {
			await runSaveBeforeLeave()
			dispatch(openTab(breadcrumbNode, true))
		},
		[dispatch, runSaveBeforeLeave],
	)

	const handleViewHistory = useCallback(() => {
		handleMenuClose()
		setHistoryActive(true)
	}, [handleMenuClose])

	const handleCloseHistory = useCallback(() => {
		setHistoryActive(false)
		setSelectedVersion(versionMax)
	}, [versionMax])

	const edit = async () => {
		dispatch(setTabPermanent(node as StructureDto))
		await dispatch(lockNode(node as StructureDto))
		await onEdit(node)
		dispatch(editTab(node as StructureDto))
	}

	const lock = async () => {
		await dispatch(lockNode(node as StructureDto))
		dispatch(setTabPermanent(node as StructureDto))
	}

	useEffect(() => {
		if (editMode && !isEditable(node as StructureDto, user)) {
			dispatch(cancelEditingTab(node as StructureDto))
		}
	}, [dispatch, editMode, node, user])

	const breadcrumbs = useMemo(
		() => (nodes ? getBreadcrumbs(node as StructureDto, nodes) : []),
		[node, nodes],
	)

	const canDelete =
		node.type === StructureObjectDto.TypeEnum.SYSTEM
			? auth.canDeleteSystem()
			: isEditable(node as StructureDto, user)

	const canExportStructure = hasStructureViewPermission(node as StructureDto)
	const canImport =
		hasStructureEditPermission(node as StructureDto) &&
		isImportableNodeType(node.type)

	const locked = isLocked(node as StructureDto)
	const lockedByMe = isLockedByMe(node as StructureDto, user)
	const editable = isEditable(node as StructureDto, user)

	const isHistoryViewMode = historyActive && selectedVersion !== versionMax

	const getStatusChips = () => {
		const chips: React.ReactNode[] = []

		// Version chip - same row; when history open shows selected version, otherwise latest
		chips.push(
			<Chip
				key="version"
				size="small"
				label={`v${historyActive ? selectedVersion : versionMax}`}
				color={historyActive && isHistoryViewMode ? 'warning' : 'default'}
				icon={<HistoryIcon />}
				variant={historyActive && isHistoryViewMode ? 'filled' : 'outlined'}
				sx={{ fontWeight: 500 }}
			/>
		)

		// Lock status chip - show when locked
		if (locked && !lockedByMe && !editMode) {
			chips.push(
				<Chip
					key="locked"
					size="small"
					label={`${t('STATUS_LOCKED')} - ${node.lockUserName}`}
					color="error"
					icon={<LockIcon />}
					variant="outlined"
					sx={{ fontWeight: 500 }}
				/>
			)
		}

		// Locked by you chip - show when locked by current user (including during edit)
		if (locked && lockedByMe && !editMode) {
			chips.push(
				<Chip
					key="locked-by-me"
					size="small"
					label={t('LOCKED_BY_YOU')}
					color="info"
					icon={<LockIcon />}
					variant="outlined"
					sx={{ fontWeight: 500 }}
				/>
			)
		}

		// Edit mode chip - only when unified status is not used (autosaveCountdown undefined = legacy)
		if (showEditSaveStatus && editMode && autosaveCountdown === undefined) {
			chips.push(
				<Chip
					key="editing-status"
					size="small"
					label={dirty ? t('AUTOSAVE_DIRTY') : t('AUTOSAVE_SAVED')}
					color={dirty ? 'default' : 'success'}
					variant="outlined"
					sx={{
						fontWeight: 400,
						borderColor: 'divider',
						...(dirty && { color: 'text.secondary' }),
					}}
				/>
			)
		}

		return chips.length > 0 ? chips : null
	}

	return (
		<Box
			sx={{
				bgcolor: 'background.paper',
				borderBottom: 1,
				borderColor: 'divider',
			}}
		>
			{/* Single row: breadcrumbs + (version info + history or normal actions) + menu */}
			<Box className="flex items-center gap-3 px-4 py-2 flex-wrap" sx={{ minHeight: 52 }}>
				{/* Breadcrumbs + Title */}
				<Box className="flex items-center gap-2 min-w-0 flex-shrink" sx={{ maxWidth: historyActive ? '60%' : '50%' }}>
					<CollapsibleBreadcrumbs
						breadcrumbs={breadcrumbs}
						currentNode={node}
						onBreadcrumbClick={handleBreadcrumbClick}
					/>
				</Box>

				{/* Spacer to push right-side content */}
				<Box sx={{ flex: 1 }} />

				{/* Version info - always in this row */}
				{currentVersionInfo && (
					<Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
						{t('LAST_VERSION', [currentVersionInfo.version, currentVersionInfo.authorName])}
						{currentVersionInfo.created && (
							<>&nbsp;{toLocaleString(currentVersionInfo.created)}</>
						)}
					</Typography>
				)}

				{/* Right-side content: history controls when open, otherwise chips + actions. Order (L→R): version info (above) | env | version select | Restore vN (merged) | X close | three dots */}
				{historyActive ? (
					<>
						<SelectEnv replacing={replacing} deployments={currentVersionDeployments} />
						<Box className="flex items-center gap-0.5" sx={{ flexShrink: 0 }}>
							<IconButton
								size="small"
								onClick={() => setSelectedVersion((prev) => prev - 1)}
								disabled={versionMin >= selectedVersion || replacing}
							>
								<RemoveIcon fontSize="small" />
							</IconButton>
							<Select
								size="small"
								value={selectedVersion}
								onChange={(e: SelectChangeEvent<number>) => setSelectedVersion(Number(e.target.value))}
								disabled={replacing}
								sx={{ minWidth: 70, '& .MuiSelect-select': { py: 0.5 } }}
							>
								{versions.map((v) => (
									<MenuItem key={v} value={v}>
										{v}
									</MenuItem>
								))}
							</Select>
							<IconButton
								size="small"
								onClick={() => setSelectedVersion((prev) => prev + 1)}
								disabled={versionMax <= selectedVersion || replacing}
							>
								<AddIcon fontSize="small" />
							</IconButton>
						</Box>
						<RestoreVersionDialog
							onRestore={handleRestore}
							replacing={replacing}
							disabled={selectedVersion >= versionMax}
							selectedVersion={selectedVersion}
						/>
						<IconButton size="small" onClick={handleCloseHistory} disabled={replacing} aria-label={t('CLOSE_HISTORY')}>
							<CloseIcon fontSize="small" />
						</IconButton>
					</>
				) : (
					<>
						<Box className="flex items-center gap-1">
							{getStatusChips()}
						</Box>
						{additionalButtons}
						{/* Action Buttons */}
						{!editMode && (
					<Box className="flex items-center gap-1">
						<Tooltip title={!editable ? '' : t('EDIT')}>
							<span>
								<Button
									variant="outlined"
									size="small"
									startIcon={<EditIcon />}
									onClick={edit}
									disabled={!editable}
									sx={{ textTransform: 'none' }}
								>
									{t('EDIT')}
								</Button>
							</span>
						</Tooltip>

						{locked && shouldShowUnlockButton(node as StructureDto, user) ? (
							<DialogWrapper
								dialog={(opened, onClose) =>
									opened && <UnlockModal node={node as StructureDto} onClose={onClose} />
								}
							>
								{(onClick) => (
									<Tooltip title={t('UNLOCK')}>
										<span>
											<Button
												variant="outlined"
												size="small"
												color="warning"
												startIcon={<LockOpenIcon />}
												onClick={onClick}
												disabled={
													!canUnlockStructure(node as StructureDto, user)
												}
												sx={{ textTransform: 'none' }}
											>
												{t('UNLOCK')}
											</Button>
										</span>
									</Tooltip>
								)}
							</DialogWrapper>
						) : !locked ? (
							<Tooltip title={t('LOCK')}>
								<span>
									<Button
										variant="outlined"
										size="small"
										color="warning"
										startIcon={<LockIcon />}
										onClick={lock}
										disabled={
											!hasStructureEditPermission(node as StructureDto)
										}
										sx={{ textTransform: 'none' }}
									>
										{t('LOCK')}
									</Button>
								</span>
							</Tooltip>
						) : null}
					</Box>
				)}

				{/* Edit Mode Buttons */}
				{editMode && (
					<Box className="flex items-center gap-1">
						{/* MDD-2599: Single edit status field when autosaveCountdown is provided */}
						{showEditSaveStatus && autosaveCountdown !== undefined && (
							<Box
								component="span"
								sx={{
									display: 'inline-flex',
									alignItems: 'center',
									gap: 0.75,
									maxWidth: '100%',
									minWidth: 0,
								}}
							>
								{dirty && (
									<Box
										component="span"
										aria-hidden
										sx={(theme) => ({
											width: 5,
											height: 5,
											borderRadius: '50%',
											flexShrink: 0,
											bgcolor: theme.palette.warning.main,
											opacity: 0.35,
										})}
									/>
								)}
								<Typography
									component="span"
									variant="caption"
									sx={{
										color: 'text.secondary',
										fontWeight: 400,
										whiteSpace: 'nowrap',
									}}
								>
									{!dirty
										? t('EDIT_STATUS_NO_CHANGES')
										: autosaveCountdown !== null
											? t('EDIT_STATUS_SAVING_IN', [String(autosaveCountdown)])
											: t('EDIT_STATUS_UNSAVED')}
								</Typography>
							</Box>
						)}
						<DialogWrapper
							dialog={(opened, onClose) =>
								opened && (
									<PushModal
										node={node as StructureDto}
										onClose={onClose}
										onExitEditMode={onCancel}
										beforePush={beforePush}
										afterPush={afterPush}
									/>
								)
							}
						>
							{(onClick) => (
								<Button
									variant="contained"
									size="small"
									color="success"
									startIcon={<PublishIcon />}
									onClick={onClick}
									sx={{ textTransform: 'none' }}
									data-testid="push-changes"
								>
									{t('PUSH')}
								</Button>
							)}
						</DialogWrapper>

						<DialogWrapper
							dialog={(opened, onClose) =>
								opened && (
									<CancelEditModal
										node={node as StructureDto}
										onClose={onClose}
										onConfirm={onCancel}
										customDiagramWarning={customDiagramWarning}
									/>
								)
							}
						>
							{(onClick) => (
								<Button
									variant="outlined"
									size="small"
									color="error"
									startIcon={<CancelIcon />}
									onClick={onClick}
									sx={{ textTransform: 'none' }}
									data-testid="cancel-changes"
								>
									{t('CANCEL')}
								</Button>
							)}
						</DialogWrapper>
					</Box>
				)}
					</>
				)}

				{/* Menu Button */}
				<Tooltip title={t('MORE_ACTIONS')}>
					<IconButton
						onClick={handleMenuOpen}
						size="small"
						sx={{
							bgcolor: menuOpen ? 'primary.main' : 'transparent',
							color: menuOpen ? 'white' : 'text.secondary',
							'&:hover': {
								bgcolor: menuOpen ? 'primary.dark' : 'action.hover',
							},
						}}
						aria-label={t('MORE_ACTIONS')}
					>
						<MoreVertIcon />
					</IconButton>
				</Tooltip>

				<Menu
					anchorEl={menuAnchor}
					open={menuOpen}
					onClose={handleMenuClose}
					transformOrigin={{ horizontal: 'right', vertical: 'top' }}
					anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
					slotProps={{
						paper: {
							elevation: 3,
							sx: { minWidth: 200, mt: 0.5 },
						},
					}}
				>
					{/* View History */}
					<MenuItem
						onClick={handleViewHistory}
						disabled={historyList.isLoading || !historyList.data?.length || historyActive}
					>
						<ListItemIcon>
							<HistoryIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText>{t('VIEW_HISTORY')}</ListItemText>
					</MenuItem>

					{additionalMenuItems}

					<Divider />

					{/* Export / Import — same as tree context menu */}
					<MenuItem
						disabled={!canExportStructure || node.id == null}
						onClick={() => {
							handleMenuClose()
							setDownload(true)
						}}
					>
						<ListItemIcon>
							<DownloadIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText>{t('EXPORT')}</ListItemText>
					</MenuItem>

					{canImport && (
						<MenuItem
							onClick={() => {
								handleMenuClose()
								setImportModalOpen(true)
							}}
						>
							<ListItemIcon>
								<FileUploadIcon fontSize="small" />
							</ListItemIcon>
							<ListItemText>{t('IMPORT')}</ListItemText>
						</MenuItem>
					)}

					{/* Permissions (structure ACL — same as tree context menu) */}
					{hasNodePermissions(node.type) &&
						hasStructureManageAccessPermission(node as StructureDto) &&
						node.id != null && (
							<>
								<Divider />
								<MenuItem
									onClick={() => {
										handleMenuClose()
										setAclModalNode(node as StructureDto)
										setAclModalTargetId(null)
									}}
								>
									<ListItemIcon>
										<SecurityIcon fontSize="small" />
									</ListItemIcon>
									<ListItemText>{t('BUTTON_PERMISSIONS')}</ListItemText>
								</MenuItem>
							</>
						)}

					{/* Delete */}
					{canDelete && (
						<DialogWrapper
							dialog={(opened, onClose) =>
								opened && <DeleteModal node={node as StructureDto} onClose={onClose} />
							}
						>
							{(onClick) => (
								<>
									<Divider />
									<MenuItem
										onClick={() => {
											handleMenuClose()
											onClick()
										}}
										sx={{ color: 'error.main' }}
									>
										<ListItemIcon>
											<DeleteIcon fontSize="small" color="error" />
										</ListItemIcon>
										<ListItemText>{t('DELETE')}</ListItemText>
									</MenuItem>
								</>
							)}
						</DialogWrapper>
					)}
				</Menu>

				{download && node.id != null && (
					<ExportStructure
						nodeId={node.id}
						onDownloadFinish={() => setDownload(false)}
					/>
				)}

				{importModalOpen && (
					<ImportStructureModal
						node={node as StructureDto}
						onClose={() => setImportModalOpen(false)}
					/>
				)}

				{aclModalNode?.id != null && (
					<StructureAclModal
						open={!!aclModalNode}
						onClose={handleCloseAclModal}
						structureId={aclModalTargetId ?? aclModalNode.id}
						structureName={aclModalNode.name}
						parentStructureId={aclModalNode.parentStructureId}
						parentStructureName={
							aclModalNode.parentStructureId != null
								? nodes[aclModalNode.parentStructureId]?.name ??
									nodes[aclModalNode.parentStructureId]?.code
								: undefined
						}
						onOpenParentPermissions={
							aclModalNode.parentStructureId != null &&
							nodes[aclModalNode.parentStructureId]
								? () => {
										setAclModalNode(nodes[aclModalNode!.parentStructureId!])
										setAclModalTargetId(null)
									}
								: undefined
						}
						onOpenStructurePermissions={(targetStructureId) => {
							const target = nodes[targetStructureId]
							if (target) setAclModalNode(target)
							setAclModalTargetId(targetStructureId)
						}}
						onAclMutated={handleAclMutated}
					/>
				)}
			</Box>
		</Box>
	)
}
